const COORDINATE_LOCATION_PATTERN = /^[-+]?\d+(?:\.\d+)?\s*,\s*[-+]?\d+(?:\.\d+)?$/;
const UNREGISTERED_INSTALL_LOCATION_LABEL = '설치장소 미등록';

export interface MarkerTitleSource {
  constructionEquipment?: unknown;
  deviceName?: string | null;
  deviceType?: string | null;
  displayDeviceName?: string | null;
  groupName?: string | null;
  id?: string | null;
  isConstructionEquipment?: boolean | null;
  location?: string | null;
  name?: string | null;
  workerName?: string | null;
}

/** 지도 타이틀 후보 문자열을 공백 없는 유효 값으로 정규화합니다. */
function readMarkerTitleString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized && normalized !== '-' ? normalized : null;
}

/** 건설장비 메타에서 등록 장비명과 장비 코드를 안전하게 읽습니다. */
function readConstructionEquipmentTitleParts(value: unknown): { code: string | null; name: string | null } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { code: null, name: null };
  }

  const record = value as Record<string, unknown>;
  return {
    code: readMarkerTitleString(record.equipment_code) ?? readMarkerTitleString(record.equipmentCode),
    name: readMarkerTitleString(record.equipment_name) ?? readMarkerTitleString(record.equipmentName),
  };
}

/** 지도 타이틀에 사용할 설치장소 문자열을 정규화합니다. */
export function normalizeMarkerInstallLocation(value: unknown): string {
  if (typeof value !== 'string') {
    return '-';
  }

  const normalized = value.trim();
  return normalized && normalized !== UNREGISTERED_INSTALL_LOCATION_LABEL ? normalized : '-';
}

/** 과거 실시간 메시지의 설치장소 fallback으로 들어온 좌표 문자열인지 판별합니다. */
export function isCoordinateMarkerLocation(value: string): boolean {
  return COORDINATE_LOCATION_PATTERN.test(value.trim());
}

/** 근로자 이름의 첫 글자와 마지막 글자만 남기고 가운데를 마스킹합니다. */
export function maskWorkerMarkerName(value: string): string {
  const characters = Array.from(value.trim());
  if (characters.length === 0) {
    return '-';
  }
  if (characters.length === 1) {
    return '*';
  }
  if (characters.length === 2) {
    return `${characters[0]}*`;
  }

  return `${characters[0]}${'*'.repeat(characters.length - 2)}${characters[characters.length - 1]}`;
}

/** 마커 유형별 우선순위에 따라 타이틀 앞부분의 표시 이름을 계산합니다. */
export function resolveMarkerTitleDisplayName(item: MarkerTitleSource): string {
  const deviceName = readMarkerTitleString(item.deviceName) ?? readMarkerTitleString(item.displayDeviceName);
  const fallbackName = readMarkerTitleString(item.name) ?? readMarkerTitleString(item.id);
  const isWorker = readMarkerTitleString(item.deviceType)?.toUpperCase() === 'WORKER';
  if (isWorker) {
    const workerName = readMarkerTitleString(item.workerName) ?? deviceName ?? fallbackName;
    return workerName ? maskWorkerMarkerName(workerName) : '-';
  }

  const constructionEquipment = readConstructionEquipmentTitleParts(item.constructionEquipment);
  const isConstructionEquipment = item.isConstructionEquipment === true || constructionEquipment.name !== null || constructionEquipment.code !== null;
  if (isConstructionEquipment) {
    return constructionEquipment.name ?? deviceName ?? constructionEquipment.code ?? fallbackName ?? '-';
  }

  const groupName = readMarkerTitleString(item.groupName);
  return groupName ?? deviceName ?? fallbackName ?? '-';
}

/** 표시 이름과 유효한 설치장소를 하이픈 구분 형식으로 조합합니다. */
export function resolveMarkerTitleText(item: MarkerTitleSource): string {
  const displayName = resolveMarkerTitleDisplayName(item);
  const normalizedLocation = normalizeMarkerInstallLocation(item.location);
  const location = normalizedLocation !== '-' && !isCoordinateMarkerLocation(normalizedLocation) ? normalizedLocation : null;
  const titleParts = [displayName === '-' ? null : displayName, location].filter((value): value is string => Boolean(value));

  return titleParts.join(' - ') || '-';
}

/** 실시간 계약의 설치장소 포함 여부를 기준으로 기존 설치장소를 안전하게 병합합니다. */
export function mergeMarkerInstallLocation(existingLocation: unknown, incomingLocation: unknown, hasIncomingLocationField: boolean): string {
  const normalizedIncoming = normalizeMarkerInstallLocation(incomingLocation);
  if (hasIncomingLocationField) {
    return isCoordinateMarkerLocation(normalizedIncoming) ? '-' : normalizedIncoming;
  }

  const normalizedExisting = normalizeMarkerInstallLocation(existingLocation);
  return normalizedExisting !== '-' && !isCoordinateMarkerLocation(normalizedExisting) ? normalizedExisting : '-';
}
